import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { projectsAPI } from '../services/api';
import ProjectForm from '../components/Projects/ProjectForm';
import FileViewer from '../components/Projects/FileViewer';
import Button from '../components/UI/Button';
import EnhancedFileViewer from '../components/Projects/EnhancedFileViewer';

const GenerateProject = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [generatedProject, setGeneratedProject] = useState(null);
  const [error, setError] = useState('');
  const [streamProgress, setStreamProgress] = useState({
    filesCompleted: 0,
    contentChunks: 0,
    currentFile: '',
    isComplete: false,
    partialFiles: [],
    totalFilesEstimate: 12
  });
  const [streamStartTime, setStreamStartTime] = useState(0);

  // 🌟 Modern loading animation states
  const [pulseAnimation, setPulseAnimation] = useState(0);
  const [glowEffect, setGlowEffect] = useState(false);

  // 🌟 Pulsing animation effect
  useEffect(() => {
    if (streaming) {
      const interval = setInterval(() => {
        setPulseAnimation(prev => (prev + 1) % 4);
      }, 800);
      return () => clearInterval(interval);
    }
  }, [streaming]);

  // 🌟 Glow effect for completion
  useEffect(() => {
    if (generatedProject && !streaming) {
      setGlowEffect(true);
      const timer = setTimeout(() => setGlowEffect(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [generatedProject, streaming]);

  // 🌟 ULTRA-MODERN: Real-time streaming handler
  const handleGenerate = async (formData) => {
    setLoading(true);
    setStreaming(true);
    setError('');
    setStreamStartTime(Date.now());

    setGeneratedProject(null);
    setGlowEffect(false);
    setStreamProgress({
      filesCompleted: 0,
      contentChunks: 0,
      currentFile: '',
      isComplete: false,
      partialFiles: [],
      totalFilesEstimate: 12
    });

    try {
      console.log('🚀 Starting AI-powered generation...');

      await projectsAPI.generateStreaming(
        formData,
        (progress) => {
          // 🌟 Optimized animation frame updates
          requestAnimationFrame(() => {
            // Update the progress handler in handleGenerate function:

            switch (progress.type) {
              case 'start':
                setStreamProgress(prev => ({
                  ...prev,
                  totalFilesEstimate: progress.totalFilesEstimate || 15,
                  message: progress.message
                }));
                break;

              case 'file':
                // 🌟 NEW: Individual file generated
                console.log(`📄 File generated: ${progress.file}`);
                const newFile = {
                  name: progress.file,
                  content: progress.contentPreview || 'Content being generated...'
                };

                setStreamProgress(prev => ({
                  ...prev,
                  filesCompleted: progress.filesCompleted || prev.filesCompleted,
                  currentFile: progress.file,
                  partialFiles: [...prev.partialFiles, newFile]
                }));
                break;

              case 'streaming':
                // 🌟 UPDATED: Streaming progress with partial files
                console.log(`📡 Streaming: ${progress.message}`);
                setStreamProgress(prev => ({
                  ...prev,
                  filesCompleted: progress.filesCompleted || prev.filesCompleted,
                  contentChunks: progress.contentChunks || prev.contentChunks,
                  currentFile: progress.currentFile || prev.currentFile,
                  bytesReceived: progress.bytesReceived || prev.bytesReceived,
                  message: progress.message || prev.message,
                  // Update partial files if provided
                  partialFiles: progress.partialFiles
                    ? [...prev.partialFiles, ...progress.partialFiles.filter(
                        newFile => !prev.partialFiles.some(f => f.name === newFile.name)
                      )]
                    : prev.partialFiles
                }));
                break;

              case 'complete':
                console.log('✨ Generation complete!', progress);

                // Final project data
                const finalFiles = progress.files || streamProgress.partialFiles;
                setGeneratedProject({
                  files: finalFiles,
                  files_count: finalFiles.length,
                  name: formData.name || 'AI Generated Project',
                  description: formData.description || '',
                  framework: formData.framework || 'python',
                  project_id: progress.project_id,
                  version_id: progress.version_id
                });

                // Update stream progress with final count
                setStreamProgress(prev => ({
                  ...prev,
                  filesCompleted: finalFiles.length,
                  totalFilesEstimate: finalFiles.length,
                  isComplete: true,
                  currentFile: '',
                  message: progress.message || 'Generation complete!',
                  partialFiles: finalFiles // Replace with all files
                }));

                setLoading(false);
                setStreaming(false);
                break;

              case 'error':
                setError(progress.error || 'Generation failed');
                setLoading(false);
                setStreaming(false);
                break;

              default:
                // Handle any other progress types
                console.log('📊 Other progress:', progress.type, progress);
                setStreamProgress(prev => ({
                  ...prev,
                  ...progress
                }));
            }
          });
        }
      );

    } catch (error) {
      console.error('❌ Generation error:', error);
      setError(error.message || 'Failed to generate project');
      setLoading(false);
      setStreaming(false);
    }
  };

  const handleViewProject = () => {
    if (generatedProject?.project_id) {
      navigate('/projects', {
        state: { selectedProjectId: generatedProject.project_id }
      });
    } else {
      setError('Project ID not available yet');
    }
  };

  const handleDownload = async () => {
    if (!generatedProject?.project_id) {
      setError('Project not ready for download yet');
      return;
    }

    try {
      const response = await projectsAPI.download(generatedProject.project_id);

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${generatedProject.name?.replace(/ /g, '_') || 'project'}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setError('Failed to download project');
      console.error('Download error:', error);
    }
  };

  const handleGenerateAnother = () => {
    setGeneratedProject(null);
    setStreamStartTime(0);
    setStreamProgress({
      filesCompleted: 0,
      contentChunks: 0,
      currentFile: '',
      isComplete: false,
      partialFiles: [],
      totalFilesEstimate: 12
    });
  };

  const getFilesToDisplay = () => {
    if (generatedProject?.files) {
      return generatedProject.files.slice(0, 5);
    }
    if (streamProgress.partialFiles.length > 0) {
      return streamProgress.partialFiles.slice(0, 5);
    }
    return [];
  };

  const getFilesCount = () => {
    if (generatedProject?.files_count) {
      return generatedProject.files_count;
    }
    return streamProgress.partialFiles.length;
  };

  // 🌟 Calculate progress with animation
  const getProgressPercentage = () => {
  if (streaming && streamProgress.totalFilesEstimate > 0) {
    // Show progress based on files completed
    if (streamProgress.filesCompleted > 0) {
      return Math.min(95, (streamProgress.filesCompleted / streamProgress.totalFilesEstimate) * 100);
    }
    // Fallback to time-based progress if no files yet
    if (streamStartTime > 0) {
      const elapsedSeconds = (Date.now() - streamStartTime) / 1000;
      // Show 5% per second up to 50%
      return Math.min(50, elapsedSeconds * 5);
    }
    return 10; // Initial progress
  }
  return streaming ? 30 : 100;
};

  // 🌟 Pulsing animation classes
  const pulseClasses = [
    'animate-pulse',
    'animate-bounce',
    'animate-ping',
    'animate-spin'
  ];

  return (
    <div className="space-y-8">
      {/* 🌟 Modern Header with Gradient */}
      <div className="text-center space-y-4">
        <h1 className="text-5xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent animate-gradient-x">
          Generate AI Project
        </h1>
        <p className="text-gray-600 text-lg max-w-2xl mx-auto">
          Transform your ideas into complete, production-ready projects with advanced AI
        </p>
        <div className="flex justify-center space-x-2">
          <span className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></span>
          <span className="w-3 h-3 bg-purple-500 rounded-full animate-pulse delay-75"></span>
          <span className="w-3 h-3 bg-pink-500 rounded-full animate-pulse delay-150"></span>
        </div>
      </div>

      {!generatedProject && !streaming ? (
        <div className={`bg-white/80 backdrop-blur-lg rounded-2xl border border-gray-200/50 p-8 shadow-2xl shadow-blue-500/10 ${glowEffect ? 'animate-glow' : ''}`}>
          {error && (
            <div className="mb-6 p-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-xl animate-shake">
              <div className="flex items-center">
                <div className="mr-3 w-8 h-8 bg-gradient-to-r from-red-500 to-pink-500 rounded-full flex items-center justify-center">
                  <span className="text-white">⚠️</span>
                </div>
                <p className="text-red-700 font-medium">{error}</p>
              </div>
            </div>
          )}

          <ProjectForm onSubmit={handleGenerate} loading={loading} />
        </div>
      ) : (
        <div className="space-y-8">
          {/* 🌟 Modern Status Card */}
          <div className={`relative overflow-hidden rounded-2xl ${streaming ? 'bg-gradient-to-br from-blue-500/10 via-purple-500/10 to-pink-500/10 border border-blue-200/50' : 'bg-gradient-to-br from-green-500/10 via-emerald-500/10 to-teal-500/10 border border-green-200/50'} backdrop-blur-lg p-8 shadow-2xl ${glowEffect ? 'shadow-green-500/30' : 'shadow-blue-500/20'}`}>
            {/* 🌟 Animated Background */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-shimmer"></div>

            <div className="relative z-10">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className={`relative ${streaming ? 'animate-spin-slow' : ''}`}>
                      <div className="w-16 h-16 rounded-full border-4 border-transparent bg-gradient-to-r from-blue-500 to-purple-600 p-0.5">
                        <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
                          <span className="text-2xl">
                            {streaming ? '⚡' : '✨'}
                          </span>
                        </div>
                      </div>
                      {streaming && (
                        <div className="absolute -inset-2 rounded-full border-4 border-blue-400/30 animate-ping"></div>
                      )}
                    </div>

                    <div className="flex-1">
                      <h3 className={`text-2xl font-bold ${streaming ? 'text-blue-800' : 'text-green-800'} mb-2`}>
                        {streaming ? (
                          <span className="flex items-center">
                            <span className="mr-2">🚀</span>
                            AI is Building Your Project
                            <span className="ml-2 text-2xl animate-bounce">...</span>
                          </span>
                        ) : (
                          <span className="flex items-center">
                            <span className="mr-2">🎉</span>
                            Project Generated Successfully!
                          </span>
                        )}
                      </h3>

                      <p className={`text-lg ${streaming ? 'text-blue-700' : 'text-green-700'} mb-4`}>
                        {streaming ? (
                          <span>
                            Generated <span className="font-bold text-2xl bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">{streamProgress.filesCompleted}</span> files in real-time
                            {streamProgress.currentFile && (
                              <span className="block text-sm mt-2 font-mono bg-white/50 px-3 py-2 rounded-lg border border-blue-200/50">
                                <span className="text-blue-600">Currently:</span> {streamProgress.currentFile}
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className="text-xl">
                            Your project is ready with <span className="font-bold text-2xl bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">{getFilesCount()}</span> files
                          </span>
                        )}
                      </p>

                      {/* 🌟 Modern Progress Bar */}
                      {streaming && (
                        <div className="space-y-3">
                          <div className="flex justify-between text-sm font-medium">
                            <span className="text-blue-700">Generation Progress</span>
                            <span className="text-purple-700">
                              {streamProgress.filesCompleted} / ~{streamProgress.totalFilesEstimate} files
                            </span>
                          </div>
                          <div className="relative h-3 bg-white/50 rounded-full overflow-hidden">
                            <div
                              className="absolute h-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-full transition-all duration-500 ease-out shadow-lg shadow-blue-500/50"
                              style={{
                                width: `${getProgressPercentage()}%`
                              }}
                            >
                              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>
                            </div>
                          </div>
                          <div className="flex justify-between text-xs text-blue-600">
                            <span className="flex items-center">
                              <div className="w-2 h-2 bg-blue-500 rounded-full mr-1 animate-pulse"></div>
                              Started
                            </span>
                            <span className="flex items-center">
                              <div className="w-2 h-2 bg-purple-500 rounded-full mr-1 animate-pulse delay-300"></div>
                              Estimated: {streamProgress.totalFilesEstimate} files
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* 🌟 Action Buttons */}
                <div className="flex space-x-4 ml-8">
                  {!streaming && (
                    <>
                      <Button
                        variant="outline"
                        onClick={handleGenerateAnother}
                        className="group relative overflow-hidden border-2 border-blue-300 hover:border-blue-500 transition-all duration-300"
                      >
                        <span className="relative z-10 flex items-center">
                          <span className="mr-2 group-hover:rotate-180 transition-transform duration-300">🔄</span>
                          Generate Another
                        </span>
                        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/0 via-blue-500/10 to-blue-500/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                      </Button>
                      <Button
                        onClick={handleDownload}
                        disabled={!generatedProject?.project_id}
                        className="group relative bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 shadow-lg shadow-green-500/30"
                      >
                        <span className="relative z-10 flex items-center">
                          <span className="mr-2 group-hover:animate-bounce">📥</span>
                          Download ZIP
                        </span>
                        <div className="absolute inset-0 bg-gradient-to-r from-emerald-400/0 via-emerald-400/20 to-emerald-400/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                      </Button>
                      <Button
                        onClick={handleViewProject}
                        disabled={!generatedProject?.project_id}
                        className="group relative bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-lg shadow-purple-500/30"
                      >
                        <span className="relative z-10 flex items-center">
                          <span className="mr-2 group-hover:translate-x-1 transition-transform duration-300">👁️</span>
                          View Project
                        </span>
                        <div className="absolute inset-0 bg-gradient-to-r from-pink-400/0 via-pink-400/20 to-pink-400/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* 🌟 Modern Project Preview */}
          <div className={`relative overflow-hidden rounded-2xl bg-white/90 backdrop-blur-lg border border-gray-200/50 shadow-2xl shadow-blue-500/10 ${streaming ? 'animate-pulse-gentle' : ''}`}>
            {/* 🌟 Animated border */}
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-2xl blur opacity-20 animate-gradient-x"></div>

            <div className="relative z-10">
              <div className="p-8 border-b border-gray-200/50">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      {streaming ? (
                        <span className="flex items-center">
                          <span className="mr-3">🎯</span>
                          Live Generation Preview
                          <span className="ml-2 w-2 h-2 bg-green-500 rounded-full animate-ping"></span>
                        </span>
                      ) : (
                        <span className="flex items-center">
                          <span className="mr-3">📁</span>
                          Generated Files Preview
                        </span>
                      )}
                    </h3>
                    <p className="text-gray-600">
                      {streaming ? (
                        <span className="flex items-center">
                          <span className="w-3 h-3 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full mr-2 animate-pulse"></span>
                          Streaming {getFilesCount()} files in real-time
                        </span>
                      ) : (
                        `Showing preview of ${Math.min(5, getFilesCount() || 0)} files from your project`
                      )}
                    </p>
                  </div>
                  {streaming && (
                    <div className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-full border border-blue-200/50">
                      <div className="w-2 h-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full animate-pulse"></div>
                      <span className="text-sm font-medium text-blue-700">LIVE STREAMING</span>
                    </div>
                  )}
                </div>
              </div>

              {/* 🌟 File Viewer with Animation */}
              {getFilesToDisplay().length > 0 ? (
  <div className="animate-fade-in-up">
    <EnhancedFileViewer
      files={getFilesToDisplay()}
      isEditable={!streaming} // Allow editing only when not streaming
      onSaveChanges={async (changes) => {
        try {
          console.log('Saving file changes:', changes);

          // If project has ID, save to backend
          if (generatedProject?.project_id) {
            const response = await projectsAPI.updateProjectFiles(
              generatedProject.project_id,
              changes
            );
            console.log('Files updated successfully:', response);

            // Update local state
            setGeneratedProject(prev => ({
              ...prev,
              files: prev.files.map(file => {
                const change = changes.find(c => c.name === file.name);
                return change ? { ...file, content: change.content } : file;
              })
            }));

            // Show success message
            setError('');
            alert('Changes saved successfully! A new version has been created.');
          } else {
            // If no project ID yet (still streaming), update local state only
            setGeneratedProject(prev => ({
              ...prev,
              files: prev.files.map(file => {
                const change = changes.find(c => c.name === file.name);
                return change ? { ...file, content: change.content } : file;
              })
            }));
            alert('Changes saved locally. Will be persisted when project is complete.');
          }
        } catch (error) {
          console.error('Failed to update files:', error);
          setError('Failed to save changes: ' + error.message);
        }
              }}
            />
          </div>
        ) : streaming ? (
          <div className="p-12 text-center">
            <div className="relative inline-block">
              {/* 🌟 Orbital loading animation */}
              <div className="relative w-32 h-32 mx-auto mb-8">
                <div className="absolute inset-0 rounded-full border-4 border-blue-200/50"></div>
                <div className="absolute inset-8 rounded-full border-4 border-purple-200/50 animate-ping"></div>
                <div className="absolute inset-4 rounded-full border-4 border-pink-200/50 animate-spin-slow"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg shadow-blue-500/50">
                    <span className="text-2xl text-white">AI</span>
                  </div>
                </div>

                {/* 🌟 Orbiting dots */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2">
                  <div className="w-4 h-4 bg-blue-500 rounded-full animate-orbit"></div>
                </div>
                <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2">
                  <div className="w-3 h-3 bg-purple-500 rounded-full animate-orbit-reverse delay-500"></div>
                </div>
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2">
                  <div className="w-2 h-2 bg-pink-500 rounded-full animate-orbit delay-1000"></div>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  AI is Processing Your Request
                </h4>
                <p className="text-gray-600">
                  {streamProgress.currentFile ? (
                    <span className="font-mono bg-gradient-to-r from-blue-100 to-purple-100 px-4 py-2 rounded-lg inline-block">
                      Creating: <span className="text-blue-700 font-medium">{streamProgress.currentFile}</span>
                    </span>
                  ) : (
                    'Initializing advanced AI models...'
                  )}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-12 text-center">
            <div className="text-6xl mb-6 animate-bounce">📄</div>
            <h4 className="text-xl font-bold text-gray-900 mb-2">No Files to Display</h4>
            <p className="text-gray-600">Try generating a new project</p>
          </div>
        )}
            </div>
          </div>
        </div>
      )}

      {/* 🌟 REMOVED: Loading box completely - No more "Generating project..." box */}

      {/* 🌟 Add custom animations to tailwind config */}
      <style jsx>{`
        @keyframes gradient-x {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes orbit {
          0% { transform: rotate(0deg) translateX(60px) rotate(0deg); }
          100% { transform: rotate(360deg) translateX(60px) rotate(-360deg); }
        }
        @keyframes orbit-reverse {
          0% { transform: rotate(0deg) translateX(60px) rotate(0deg); }
          100% { transform: rotate(-360deg) translateX(60px) rotate(360deg); }
        }
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes pulse-gentle {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.8; }
        }
        @keyframes glow {
          0%, 100% { box-shadow: 0 0 20px rgba(59, 130, 246, 0.3); }
          50% { box-shadow: 0 0 40px rgba(59, 130, 246, 0.6); }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
          20%, 40%, 60%, 80% { transform: translateX(5px); }
        }

        .animate-gradient-x {
          animation: gradient-x 3s ease infinite;
          background-size: 200% 200%;
        }
        .animate-spin-slow {
          animation: spin-slow 3s linear infinite;
        }
        .animate-orbit {
          animation: orbit 4s linear infinite;
        }
        .animate-orbit-reverse {
          animation: orbit-reverse 4s linear infinite;
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.5s ease-out;
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
        .animate-pulse-gentle {
          animation: pulse-gentle 2s ease-in-out infinite;
        }
        .animate-glow {
          animation: glow 2s ease-in-out infinite;
        }
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default GenerateProject;